import React, { useEffect, useState, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../store/reducers/user";

import logodark from "../../assets/images/logo.png";
import logosmdark from "../../assets/images/logo-sm-dark.png";
import darkmodemodelogo from "../../assets/images/Dark_mode_logo.png"; 
import medgeLight from "../../assets/images/medge_light.png";
import medgeDark from "../../assets/images/medge_dark.png";

// Color mode context import (Used ONLY for image asset switching)
import { ColorModeContext } from "../../index";

const mobilePanelStyle = {
  position: "absolute",
  left: "217px",
  top: 0,
  width: "200px",
  backgroundColor: "var(--canvas)",
  borderLeft: "1px solid var(--hairline)",
  height: "100vh",
  overflowY: "auto",
  zIndex: 200,
  padding: "10px",
};

const SidebarContent = ({ isCollapsed, toggleSidebar }) => {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';

  const organization = useSelector((state) => state.organization);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("authUser");
    localStorage.removeItem("token");
    localStorage.removeItem("session_Id");
    sessionStorage.clear();
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    dispatch(setUser(null));
    navigate("/");
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileChildren, setMobileChildren] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Removed DOM cloning useEffect that broke React event listeners

  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;
  const isSuperAdmin = user ? user.superadmin : false;
  const permissions = user && user.permissions ? user.permissions : [];

  const [openMenus, setOpenMenus] = useState({});
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && organization && organization.length > 0) {
      const initial = {};
      organization.forEach(org => {
        if (org.options) {
          org.options.forEach(opt => {
            if (opt.menu && opt.menu.length > 0) initial[opt.id] = true;
            if (opt.children && opt.children.length > 0) initial[opt.id] = true;
          });
        }
      });
      setOpenMenus(initial);
      setInitialized(true);
    }
  }, [organization, initialized]);

  const toggleMenu = (e, id) => {
    e.preventDefault();
    setOpenMenus(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const pathMatches = (to) => {
    if (!to || typeof to !== "string") return false;
    const norm = (str) => (str.endsWith("/") && str.length > 1 ? str.slice(0, -1) : str);
    const cur = norm(location.pathname || "/");
    const target = norm(to);
    if (target === cur) return true;
    try {
      return cur.startsWith(target) && target !== "/";
    } catch {
      return false;
    }
  };

  const isActiveItem = (item) => {
    if (!item) return false;
    if (item.to && pathMatches(item.to)) return true;
    if (item.menu && Array.isArray(item.menu)) {
      for (const child of item.menu) {
        if (isActiveItem(child)) return true;
      }
    }
    if (item.children && Array.isArray(item.children)) {
      for (const child of item.children) {
        if (isActiveItem(child)) return true;
      }
    }
    return false;
  };

  const renderMenuItems = (menu, depth = 0) => {
    return menu
      .filter((item) =>
        isSuperAdmin || permissions.some((perm) => perm.includes(item.id))
      )
      .map((item) => {
        const showLabel = depth > 0 || !(isMobile && isCollapsed);
        const linkClass = depth === 0 && isMobile && isCollapsed ? "waves-effect" : "has-arrow waves-effect";
        const active = isActiveItem(item);
        const hasChildren = item.children && item.children.length > 0;
        const isBlueHighlighted = active && !hasChildren;

        const linkStyle = {
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: "0 16px",
          height: "44px",
          margin: "4px 16px",
          background: isBlueHighlighted ? "#1F6FEB" : "transparent",
          borderRadius: "8px",
          textDecoration: "none",
          transition: "background 0.15s ease, outline 0.15s ease",
          outline: "2px solid transparent",
          outlineOffset: "-2px"
        };

        const labelStyle = {
          color: active ? "#ffffff" : "#8B9BB4",
          fontFamily: "Arial, sans-serif",
          fontWeight: active ? 600 : 500,
          fontSize: "15px",
          display: "inline-block",
          verticalAlign: "middle",
          transition: "color 0.15s ease"
        };
        
        const iconStyle = {
           display: "inline-flex", 
           alignItems: "center", 
           color: active ? "#ffffff" : "#8B9BB4",
           '& svg': { strokeWidth: 1.5 }
        };

        if (item.children && item.children.length > 0) {
          return (
            <li key={item.id}>
              <Link
                to="#"
                className={linkClass}
                aria-expanded={openMenus[item.id] ? "true" : "false"}
                onClick={(e) => {
                  if (isMobile && isCollapsed && depth === 0) {
                    e.preventDefault();
                    setMobileChildren(item.children);
                  } else {
                    toggleMenu(e, item.id);
                  }
                }}
                style={linkStyle}
                aria-current={active ? "page" : undefined}
                onMouseEnter={(e) => { if (!isBlueHighlighted) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)"; }}
                onMouseLeave={(e) => { if (!isBlueHighlighted) e.currentTarget.style.backgroundColor = "transparent"; }}
                onFocus={(e) => { e.currentTarget.style.outline = "2px solid #ffffff"; }}
                onBlur={(e) => { e.currentTarget.style.outline = "2px solid transparent"; }}
              >
                <span style={iconStyle}>
                  {item.icon && item.icon}
                </span>
                {showLabel && <span className="ms-1" style={labelStyle}>{item.Label}</span>}
              </Link>
              {(!isMobile || !isCollapsed || depth > 0) && (
                <ul className="sub-menu" style={{ marginLeft: "0", paddingLeft: "0", marginTop: "4px", display: openMenus[item.id] ? "block" : "none" }}>
                  {renderMenuItems(item.children, depth + 1)}
                </ul>
              )}
            </li>
          );
        } else {
          return (
            <li key={item.id}>
              <Link 
                 to={item.to || "#"} 
                 className="waves-effect" 
                 style={linkStyle} 
                 aria-current={active ? "page" : undefined}
                 onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)"; }}
                 onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = "transparent"; }}
                 onFocus={(e) => { e.currentTarget.style.outline = "2px solid #ffffff"; }}
                 onBlur={(e) => { e.currentTarget.style.outline = "2px solid transparent"; }}
              >
                <span style={iconStyle}>
                  {item.icon && item.icon}
                </span>
                {showLabel && <span className="ms-1" style={labelStyle}>{item.Label}</span>}
              </Link>
            </li>
          );
        }
      });
  };

  return (
    <div
      id="sidebar-menu"
      style={{
        backgroundColor: isDark ? "#0b1120" : "var(--vault-navy)",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        position: "relative",
        transition: "background-color 0.3s ease",
        fontFamily: "Calibri, sans-serif",
        overflow: "hidden",
      }}
    >
      <div style={{ 
        display: "flex",
        alignItems: "center",
        justifyContent: isCollapsed ? "center" : "space-between",
        height: "64px",
        padding: isCollapsed ? "0" : "0 24px",
        borderBottom: "1px solid rgba(255,255,255,0.05)"
      }}>
        <div style={{ 
          width: isCollapsed ? "0px" : "140px", 
          overflow: "hidden", 
          transition: "width 0.3s ease",
          opacity: isCollapsed ? 0 : 1
        }}>
          <Link to="/home" style={{ display: "block", textDecoration: "none" }}>
            <img
              src={darkmodemodelogo}
              alt="Logo"
              style={{
                width: "100%",
                minWidth: "140px",
                display: "block",
              }}
            />
          </Link>
        </div>
        <div 
          onClick={toggleSidebar}
          style={{ 
            cursor: "pointer", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            width: "40px", 
            height: "40px",
            border: isCollapsed ? "1px solid rgba(255,255,255,0.2)" : "none",
            borderRadius: "6px",
            color: "white",
            marginRight: isCollapsed ? "0" : "-8px"
          }}
        >
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </div>
      </div>
      
      <div id="side-menu-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", scrollbarWidth: "none", msOverflowStyle: "none" }}>
        <style>
          {`
            #side-menu-scroll::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
        <ul
          id="side-menu"
          style={{ paddingLeft: 0, listStyle: "none", margin: 0, paddingBottom: "16px" }}
          className={!isMobile || !isCollapsed ? "metismenu list-unstyled" : "list-unstyled"}
        >
        {organization && organization.length > 0 ? (
          organization.map((org) => {
            if (org.id === "hospital") {
              const itemsToRender = isCollapsed 
                ? org.options.flatMap(opt => opt.menu && opt.menu.length > 0 ? opt.menu : (opt.to ? [opt] : []))
                : org.options;

              return itemsToRender
                .filter((option) =>
                  isSuperAdmin || permissions.some((perm) => perm.includes(option.id))
                )
                .map((option) => {
                  const optionActive = isActiveItem(option);
                    const hasChildren = option.menu && option.menu.length > 0;
                    const isBlueHighlighted = optionActive && !hasChildren;
                    return (
                      <li key={option.id}>
                        <Link
                          to={hasChildren ? "#" : (option.to || "#")}
                          className={hasChildren ? "has-arrow waves-effect" : "waves-effect"}
                          aria-expanded={hasChildren ? (openMenus[option.id] ? "true" : "false") : undefined}
                          title={isCollapsed ? option.Label : ""}
                          onClick={(e) => {
                            if (hasChildren) {
                              if (isCollapsed) {
                                e.preventDefault();
                                toggleSidebar();
                              } else {
                                toggleMenu(e, option.id);
                              }
                            }
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 16,
                            padding: isCollapsed ? "16px 0" : (hasChildren ? "16px 24px 8px 32px" : "0 16px"),
                            justifyContent: isCollapsed ? "center" : "flex-start",
                            height: hasChildren && !isCollapsed ? "auto" : "44px",
                            margin: hasChildren && !isCollapsed ? "4px 0 0 0" : "4px 16px",
                            background: isBlueHighlighted ? "#1F6FEB" : "transparent",
                            borderRadius: "8px",
                            textDecoration: "none",
                            transition: "background 0.15s ease, outline 0.15s ease",
                            outline: "2px solid transparent",
                            outlineOffset: "-2px"
                          }}
                          onMouseEnter={(e) => { if (!isBlueHighlighted && !hasChildren) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)"; }}
                          onMouseLeave={(e) => { if (!isBlueHighlighted && !hasChildren) e.currentTarget.style.backgroundColor = "transparent"; }}
                          onFocus={(e) => { e.currentTarget.style.outline = "2px solid #ffffff"; }}
                          onBlur={(e) => { e.currentTarget.style.outline = "2px solid transparent"; }}
                        >
                          {(option.icon && (!hasChildren || isCollapsed)) && (
                            <span style={{ display: "inline-flex", alignItems: "center", color: optionActive ? "#ffffff" : "#8B9BB4", '& svg': { strokeWidth: 1.5 } }}>
                              {option.icon}
                            </span>
                          )}
                          <span 
                            className="ms-1" 
                            style={hasChildren ? {
                              display: isCollapsed ? "none" : "block",
                              color: "#8B9BB4",
                              fontFamily: "Arial, sans-serif",
                              fontWeight: 700,
                              fontSize: "12px",
                              textTransform: "uppercase",
                              letterSpacing: "1px"
                            } : {
                              display: isCollapsed ? "none" : "inline-block",
                              color: optionActive ? "#ffffff" : "#8B9BB4",
                              fontFamily: "Arial, sans-serif",
                              fontWeight: optionActive ? 600 : 500,
                              fontSize: "15px"
                            }}
                          >
                            {option.Label}
                          </span>
                        </Link>
                        {option.menu && option.menu.length > 0 && !isCollapsed && (
                          <ul className="sub-menu" style={{ marginLeft: "0", paddingLeft: "0", marginTop: "4px", display: openMenus[option.id] ? "block" : "none" }}>
                            {renderMenuItems(option.menu, 1)}
                          </ul>
                        )}
                      </li>
                    );
                });
            }
            return null;
          })
        ) : (
          <li style={{ padding: "10px 15px", color: "var(--slate)" }}>No modules available</li>
        )}
        </ul>
      </div>

      {/* Flyout Sub-menu Panel for Mobile mode */}
      {isMobile && mobileChildren && (
        <div style={mobilePanelStyle}>
          <div 
            style={{ 
              color: "var(--edge-blue)", 
              marginBottom: "10px", 
              cursor: "pointer", 
              fontWeight: 600 
            }} 
            onClick={() => setMobileChildren(null)}
          >
            &larr; Back
          </div>
          <ul className="list-unstyled">
            {renderMenuItems(mobileChildren, 1)}
          </ul>
        </div>
      )}

      {/* Bottom Actions (Logout) */}
      <div
        style={{
          padding: "8px 0 4px 0",
          borderTop: "1px solid rgba(255, 255, 255, 0.08)",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={handleLogout}
          title={isCollapsed ? "Logout" : ""}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: isCollapsed ? "center" : "flex-start",
            gap: 16,
            width: isCollapsed ? "44px" : "calc(100% - 32px)",
            height: "44px",
            margin: isCollapsed ? "0 auto" : "0 16px",
            padding: isCollapsed ? "0" : "0 16px",
            background: "transparent",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            color: "#EF4444",
            textDecoration: "none",
            transition: "background 0.15s ease, color 0.15s ease",
            outline: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(239, 68, 68, 0.12)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
          onFocus={(e) => {
            e.currentTarget.style.outline = "2px solid rgba(239, 68, 68, 0.5)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.outline = "none";
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ display: "block" }}
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </span>
          {!isCollapsed && (
            <span
              style={{
                fontFamily: "Arial, sans-serif",
                fontWeight: 600,
                fontSize: "15px",
                whiteSpace: "nowrap",
                letterSpacing: "0.2px",
              }}
            >
              Logout
            </span>
          )}
        </button>
      </div>

      {/* Footer Branding */}
      <div
        style={{
          display: isCollapsed ? "none" : "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          backgroundColor: isDark ? "#0b1120" : "var(--vault-navy)",
          textAlign: "center",
          padding: "6px 0 10px 0",
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 12, color: "var(--slate)", whiteSpace: "nowrap", fontWeight: 500 }}>
          POWERED BY
        </span>
        <div style={{ display: "flex", alignItems: "baseline", fontFamily: "Arial", fontWeight: "bold" }}>
           <span style={{ color: "#ffffff", fontSize: "14px" }}>mEDGE</span>
           <span style={{ color: "var(--edge-blue)", fontSize: "14px" }}>.io</span>
        </div>
      </div>
    </div>
  );
};

export default SidebarContent;